import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import App from './App';
import { theme } from './styles/theme';
import { AuthProvider } from './contexts/AuthContext';
import { StudentViewProvider } from './contexts/StudentViewContext';
import { StudentLayoutProvider } from './contexts/StudentLayoutContext';
import { AdminTeacherViewProvider } from './contexts/AdminTeacherViewContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { initializeCsrf } from './services/api';
import { shouldRetryQuery } from './utils/queryRetry';
import { InstallPrompt, OfflineIndicator, PwaGate, PwaUpdatePrompt } from './components/pwa';
import CookieBanner from './components/CookieBanner';
import { installZodErrorMap } from './utils/zodErrorMap';
import './styles/global.css';

// #664 D8: zod 3's plain-English wording for the forms' own checks
installZodErrorMap();

// Unregister service workers in development to prevent caching conflicts
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
      console.log('[Dev] Unregistered service worker:', registration.scope);
    }
  });
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: shouldRetryQuery, // never resend a refused or invalid read (#272)
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize CSRF token early (non-blocking)
initializeCsrf();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <AuthProvider>
              <AdminTeacherViewProvider>
                <StudentViewProvider>
                  <StudentLayoutProvider>
                    <CookieConsentProvider>
                      <App />
                      {/* PWA Components - scoped to login + portal routes */}
                      <PwaGate>
                        <InstallPrompt />
                        <OfflineIndicator />
                      </PwaGate>
                      {/* Update prompt — runs everywhere so all users (incl. admins) get reload toast */}
                      <PwaUpdatePrompt />
                      {/* Cookie Banner - appears on all pages */}
                      <CookieBanner />
                    </CookieConsentProvider>
                  </StudentLayoutProvider>
                </StudentViewProvider>
              </AdminTeacherViewProvider>
            </AuthProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
